/**
 * Response shapes, mirrored from hrms-backend/src/routes/*.js.
 *
 * Field names are the backend's snake_case wherever the backend returns
 * snake_case — renaming them here would only add a translation layer that has
 * to be kept in sync with two sources of truth.
 */

export type Role = "admin" | "manager" | "employee";
export type WorkMode = "onsite" | "hybrid" | "remote";
export type EmploymentType = "FTE" | "Intern";
export type Gender = "Female" | "Male" | "Other";
/** 'withdrawn' means every working day of the request was handed back. */
export type LeaveStatus = "pending" | "approved" | "rejected" | "withdrawn";
export type AttendanceMode = "onsite" | "remote";

/**
 * Work-from-home requests have no 'withdrawn': a pending one is deleted by the
 * person who raised it, and an approved one only an approver can change. Nothing
 * is spent on a WFH day, so there is nothing to hand back.
 */
export type WfhStatus = "pending" | "approved" | "rejected";

/** Leave types the backend actually accepts (utils/leaveEntitlements.js + CompOff). */
export type LeaveType =
  | "EL"
  | "CL"
  | "SL"
  | "Menstrual"
  | "Special"
  | "OptionalHoliday"
  | "CompOff"
  | "LWP";

export interface SessionUser {
  id: number;
  email: string;
  name: string | null;
  designation: string;
  role: Role;
  teamId: number | null;
  team_name: string | null;
  reporting_manager_name: string | null;
  work_mode: WorkMode;
  /** Drives leave-policy eligibility. Sent by login and /auth/me — never inferred
   *  from which balances happened to come back. */
  employment_type: EmploymentType;
  /** True only for the admin accounts leave from managers (and the primary
   *  HR & Admin account) can be routed to for decision. */
  is_people_function_approver: boolean;
}

export interface LoginResponse {
  token: string;
  mustResetPassword: boolean;
  user: SessionUser;
}

export interface Team {
  id: number;
  name: string;
}

/** GET /api/admin/users */
export interface AdminUser {
  id: number;
  email: string;
  name: string | null;
  designation: string;
  role: Role;
  // Real boolean since the backend moved to Postgres. It was 0/1 while the data
  // lived in SQLite, where every column was effectively text or integer.
  must_reset_password: boolean;
  created_at: string;
  date_of_birth: string | null;
  date_joined: string | null;
  work_mode: WorkMode;
  employment_type: EmploymentType;
  team_id: number | null;
  team_name: string | null;
  gender: Gender | null;
  is_people_function_approver: boolean;
}

/** GET /api/manager/team */
export interface TeamMember {
  id: number;
  email: string;
  name: string | null;
  designation: string;
  role: Role;
  work_mode: WorkMode;
  must_reset_password: boolean;
  created_at: string;
}

/**
 * One work day, as the backend projects it from that day's events.
 *
 * `total_minutes` is the figure the day is judged on — the sum of its CLOSED
 * sessions. `check_in` and `check_out` are the day's bookends (first arrival,
 * last departure) and once a day has more than one session the gap between them
 * is NOT the hours worked, because it spans the breaks.
 *
 * `status` says which KIND of day this is:
 *
 *   null         a clocked day — judged on total_minutes
 *   'present'    a declared remote full day  — no minutes, and none expected
 *   'half'       a declared remote half day  — likewise
 *   'inactive'   a deliberate "not working today"
 *
 * A declared day carries total_minutes 0 because nobody measured any. Reading it
 * as "worked nothing" is the mistake this field exists to prevent, so anything
 * deciding what a day was worth must consult status before minutes.
 */
export type AttendanceStatus = "inactive" | "present" | "half" | null;

export interface AttendanceRecord {
  user_id: number;
  date: string;
  check_in: string | null;
  check_out: string | null;
  mode: AttendanceMode | null;
  status: AttendanceStatus;
  total_minutes: number;
}

/** One check-in/check-out pair. `check_out` is null while it's still running. */
export interface AttendanceSession {
  check_in: string;
  check_out: string | null;
  minutes: number;
  in_source: "self" | "hr" | "import";
  out_source: "self" | "hr" | "import" | null;
  /** Present on the HR/manager log only — the handle its edit endpoints take. */
  id?: number | null;
}

/** One button press, as the audit trail reports it. Voided ones are included. */
export interface AttendanceEvent {
  id: number;
  at: string;
  type: "in" | "out" | "off" | "present";
  work_date: string;
  mode: AttendanceMode;
  /** Only on a 'present' declaration: how much of the day it claims. */
  portion?: "full" | "half" | null;
  source: "self" | "hr" | "import";
  recorded_at: string;
  recorded_by_name: string | null;
  voided_at: string | null;
  voided_by_name: string | null;
}

export interface AttendancePerson {
  id: number;
  name: string | null;
  email?: string;
  role?: Role;
  work_mode?: WorkMode;
  team_name?: string | null;
}

/**
 * One person, one day, covered by a live leave request — the shape every
 * attendance view consumes leave in. Mirrors utils/leaveCalendar.js.
 *
 * Only 'approved' and 'pending' ever arrive: a rejected request leaves the day
 * exactly as it was, so it is dropped server-side rather than filtered here.
 * A day covered by a span carries a row even if it is a Sunday inside that span —
 * classifyDay decides whether the weekly off or the leave is what the cell should
 * say, and it needs to see both to do that.
 */
export interface LeaveDay {
  user_id: number;
  date: string;
  request_id: number;
  leave_type: LeaveType;
  /** Never 'rejected' — see above. */
  status: Exclude<LeaveStatus, "rejected">;
  reason: string | null;
}

/**
 * One person, one day, covered by a live work-from-home request. Mirrors
 * utils/wfhCalendar.js, and shaped like LeaveDay so the views can index the two
 * the same way.
 *
 * Emphatically NOT a kind of leave: this is a day worked, from a different desk.
 * It never excuses an absence and never enters a balance.
 */
export interface WfhDay {
  user_id: number;
  date: string;
  request_id: number;
  /** Never 'rejected' — a refused request leaves the day exactly as it was. */
  status: Exclude<WfhStatus, "rejected">;
  reason: string;
}

export interface AttendanceRangeResponse {
  users: AttendancePerson[];
  records: AttendanceRecord[];
  holidays?: Holiday[];
  /** One row per person-day on live leave across the range. */
  leave?: LeaveDay[];
  /** One row per person-day on a live work-from-home request. */
  wfh?: WfhDay[];
}

/** An earlier day left with a check-in and no check-out. Only HR can close it. */
export interface MissedCheckOut {
  date: string;
  check_in: string;
  mode: AttendanceMode | null;
}

/**
 * GET /api/attendance/today — the work day in progress, its sessions, and any
 * earlier day still left open.
 *
 * `workDate` is explicit because between midnight and 06:00 it is yesterday's
 * date: the shift running then belongs to yesterday, and the card has to say
 * which day it is acting on rather than leave that to be guessed.
 */
export interface TodayAttendance {
  record: (AttendanceRecord & { id: number }) | null;
  workMode: WorkMode;
  workDate: string;
  sessions: AttendanceSession[];
  /** The session running right now, if any — possibly opened on an earlier day. */
  openSession: { date: string; check_in: string; mode: AttendanceMode } | null;
  /** Earlier days with an unclosed session. Each is a half day until corrected. */
  missedCheckOuts: MissedCheckOut[];
  /** Live leave covering the work day this card is acting on, if any. */
  leave: LeaveDay | null;
  /**
   * A live work-from-home request covering the same day. When it is approved,
   * checking in records a REMOTE day rather than an onsite one, and the card
   * says so before the button is pressed rather than after.
   */
  wfh: WfhDay | null;
}

/** One person's full log, including who overrode a day and when. */
export interface AttendanceLogRow {
  date: string;
  check_in: string | null;
  check_out: string | null;
  mode: AttendanceMode | null;
  status: AttendanceStatus;
  total_minutes: number;
  edited_by: number | null;
  edited_at: string | null;
  edited_by_name: string | null;
}

/** GET /api/{admin,manager}/attendance/user/:id — the log, plus how it got there. */
export interface AttendanceLogResponse {
  user: { id: number; name: string | null; work_mode: WorkMode };
  records: AttendanceLogRow[];
  sessionsByDate: Record<string, AttendanceSession[]>;
  events: AttendanceEvent[];
  leave?: LeaveDay[];
  wfh?: WfhDay[];
}

/**
 * One in-app notification. Created as a consequence of something else happening
 * (a request, a decision, a withdrawal) — never posted directly.
 */
export interface AppNotification {
  id: number;
  kind: string;
  title: string;
  body: string | null;
  /**
   * A ROLE-NEUTRAL key, not a route: '/leave' means "the leave screen for
   * whoever is reading this", which differs per role. Pass it through
   * resolveNotificationLink() in app/nav.ts before navigating — no `/leave`
   * route exists, so using this value verbatim lands on the catch-all redirect.
   */
  link: string | null;
  subject_id: number | null;
  created_at: string;
  read_at: string | null;
}

export interface Holiday {
  id?: number;
  date: string;
  name: string;
  category?: "fixed" | "optional";
}

export interface LeaveRequest {
  id: number;
  user_id: number;
  start_date: string;
  end_date: string;
  reason: string | null;
  leave_type: LeaveType;
  status: LeaveStatus;
  decided_by: number | null;
  decided_at: string | null;
  created_at: string;
  /** Working days this request COSTS now — Sundays, 2nd/4th Saturdays and fixed
   *  holidays inside the range are never counted, and neither are days that have
   *  been withdrawn. Computed server-side on every response. */
  days: number;
  /** What it was approved for, before any withdrawal. Equal to `days` unless
   *  part of it has been handed back — which is the only way to see that it
   *  changed, since `days` simply shrinks. */
  approved_days?: number;
  /** The specific days handed back, ascending. */
  withdrawn_days?: string[];
  /** Raised after the first day it covers — leave already taken, filed afterwards. */
  filed_late?: boolean;
  /** Who decided it, by name. Present on the list endpoints; null while pending. */
  decided_by_name?: string | null;
  /** Present on the admin and manager list endpoints, absent on /leave/mine. */
  employee_name?: string | null;
  employee_email?: string;
  team_name?: string | null;
  /** Present on the admin list endpoint only — who this request belongs to,
   *  used to decide whether the current admin can act on it. */
  employee_role?: Role;
  employee_is_people_function_approver?: boolean;
}

/**
 * One work-from-home request. Mirrors hrms-backend/src/routes/wfh.js.
 *
 * Structurally similar to LeaveRequest and deliberately a separate type: there
 * is no leave_type, no entitlement, no withdrawal, and a reason is mandatory
 * rather than optional. Sharing one type would let WFH be passed to any function
 * that takes leave, which is exactly the confusion the separate table exists to
 * prevent.
 */
export interface WfhRequest {
  id: number;
  user_id: number;
  start_date: string;
  end_date: string;
  reason: string;
  status: WfhStatus;
  decided_by: number | null;
  decided_at: string | null;
  created_at: string;
  /** Working days the request covers — Sundays, 2nd/4th Saturdays and fixed
   *  holidays inside the range are never counted. Computed server-side. */
  days: number;
  /** Raised after the first day it covers — a day already worked from home. */
  filed_late?: boolean;
  /** Who decided it, by name. Null while pending. */
  decided_by_name?: string | null;
  /** Present on the team and org-wide list endpoints, absent on /wfh/mine. */
  employee_name?: string | null;
  employee_email?: string;
  employee_work_mode?: WorkMode;
  /** Org-wide endpoint only. */
  employee_role?: Role;
  team_name?: string | null;
}

/** GET /api/wfh/eligibility — whether this account can ask at all, and why not. */
export interface WfhEligibility {
  eligible: boolean;
  workMode: WorkMode | null;
  reason: string | null;
}

/**
 * GET /api/wfh/weekly-summary — the two figures the overview charts plot.
 *
 * `people` carries everyone in scope including those on zero, because "nobody
 * worked from home this week" and "we have no data" are different answers.
 * `week` is null only when a manager has no team mapped to them yet.
 */
export interface WfhWeeklySummary {
  week: { start: string; end: string } | null;
  people: { id: number; name: string | null; days: number }[];
  weeks: { start: string; end: string; days: number }[];
  weeksRequested: number;
}

/** One entitlement line. `entitlement`/`available` are null for Special Leave,
 *  whose annual number HR has deliberately left undefined. */
export interface LeaveBalanceLine {
  entitlement: number | null;
  taken: number;
  available: number | null;
  /** Present only when HR stated this balance by hand, replacing the policy
   *  calculation. `entitlement` is then the number HR set and `taken` is only
   *  leave approved since. */
  setByHr?: LeaveBalanceSetByHr;
  /** The year's total HR stated for this type, or null if none has been. */
  yearTotal?: number | null;
  /** Leave taken this calendar year. For an HR-set line it is yearTotal minus
   *  what is left, and null while no total has been stated — the leave before
   *  HR typed the balance isn't on record. Null for Comp Off. */
  takenThisYear?: number | null;
  /** HR-set lines only: leave charged since HR typed the balance. */
  takenSince?: number;
}

export interface LeaveBalanceSetByHr {
  days: number;
  year: number;
  setAt: string;
  setByName: string | null;
  note: string | null;
}

/** The leave types HR can state a balance for — mirrors OVERRIDABLE_TYPES in
 *  hrms-backend/src/utils/leaveEntitlements.js. */
export const SETTABLE_BALANCE_TYPES = ["EL", "CL", "SL", "Menstrual", "OptionalHoliday"] as const;
export type SettableBalanceType = (typeof SETTABLE_BALANCE_TYPES)[number];

/**
 * Every bucket the account has, keyed by leave type — including `CompOff`,
 * which is computed from the comp-off ledger rather than from an entitlement
 * rule. On that one line `entitlement` is days EARNED to date (working a weekly
 * off or a holiday credits it), not an annual cap, so a full meter means the
 * comp off has been used rather than that a limit is about to be breached.
 * Render it with `escalate={false}`.
 */
export type LeaveBalance = Partial<Record<LeaveType, LeaveBalanceLine>>;

/**
 * The same comp-off figures on their own, from /api/attendance/compoff-balance,
 * for the headline tiles and the attendance-side ledger view. `earned`/`used`/
 * `available` are the identical numbers `LeaveBalance.CompOff` carries as
 * `entitlement`/`taken`/`available` — one backend function produces both.
 */
export interface CompOffBalance {
  earned: number;
  used: number;
  available: number;
}

export interface LeaveSummaryMember {
  id: number;
  name: string | null;
  email: string;
  team_name?: string | null;
  employment_type: EmploymentType;
  balance: LeaveBalance;
  compOff: CompOffBalance;
}

/**
 * Days actually taken per type, for one calendar month — not an annual balance.
 * `lwp` is a computed figure (a working day with no attendance record and no
 * approved leave covering it), always present regardless of leave-type
 * eligibility, since anyone can be absent with no record.
 */
export interface LeaveMonthlySummary {
  month: string;
  byType: Partial<Record<LeaveType, number>>;
  lwp: number;
}

export interface LeaveMonthlySummaryMember extends LeaveMonthlySummary {
  id: number;
  name: string | null;
  email: string;
  team_name?: string | null;
  employment_type: EmploymentType;
}

export interface CompOffLedgerEntry {
  date: string;
  type: "Earned" | "Used";
  amount: number;
  reason: string;
  employee_name?: string;
}

export type DocType =
  | "aadhaar"
  | "pan"
  | "cancelled_cheque"
  | "offer_letter"
  | "compensation_sheet";

export interface EmployeeDocument {
  doc_type: DocType;
  original_name: string;
  mime_type: string;
  uploaded_by: number;
  uploaded_at: string;
}

export interface EmployeeProfile {
  user_id: number;
  account_number: string | null;
  account_holder_name: string | null;
  bank_name: string | null;
  ifsc_code: string | null;
  current_address: string | null;
  permanent_address: string | null;
  same_as_current: boolean;
  blood_group: string | null;
  gender: Gender | null;
  aadhaar_number: string | null;
  pan_number: string | null;
  emergency_contact_1_name: string | null;
  emergency_contact_1_number: string | null;
  emergency_contact_2_name: string | null;
  emergency_contact_2_number: string | null;
  updated_at: string | null;
}

/** GET /api/org-calendar */
export interface OrgCalendarPerson {
  id: number;
  name: string | null;
  designation: string;
  date_of_birth: string | null;
  date_joined: string | null;
}

export interface OrgCalendarResponse {
  people: OrgCalendarPerson[];
  holidays: Holiday[];
}

export interface OnboardingStatus {
  complete: boolean;
  missingProfileFields: string[];
  missingDocTypes: DocType[];
  missingDates: string[];
  dateOfBirth: string | null;
  dateJoined: string | null;
}
